// Delete-manager confirm dialog. When the backend refuses the delete (tag
// assigned / activities today) the dialog swaps to the blocked message with
// a single "Got it" button. The check runs on confirm rather than on open —
// one round-trip instead of two list reads.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManagerInUseError, type IAdminManager } from '@apis/adminManagers';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { TradePill } from '@components/ui/TradePill/TradePill';
import styles from './DeleteManagerDialog.module.css';

interface IDeleteManagerDialogProps {
  manager: IAdminManager;
  deleting: boolean;
  onConfirm: (manager: IAdminManager) => Promise<void>;
  onClose: () => void;
}

export function DeleteManagerDialog({
  manager,
  deleting,
  onConfirm,
  onClose,
}: IDeleteManagerDialogProps) {
  const { t } = useTranslation();
  const [blockedReason, setBlockedReason] = useState<'tags' | 'activities' | null>(null);

  async function handleConfirm() {
    try {
      await onConfirm(manager);
    } catch (err) {
      if (err instanceof ManagerInUseError) {
        setBlockedReason(err.reason);
        return;
      }
      onClose();
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('adminManagement.delete.title')}
      footer={
        blockedReason ? (
          <>
            <span className={styles.footerSpacer} />
            <Button variant="primary" size="md" onClick={onClose}>
              {t('adminManagement.delete.gotIt')}
            </Button>
          </>
        ) : (
          <>
            <span className={styles.footerSpacer} />
            <Button variant="secondary" size="md" onClick={onClose}>
              {t('adminManagement.dialog.cancel')}
            </Button>
            <Button variant="primary" size="md" disabled={deleting} onClick={handleConfirm}>
              {t('adminManagement.delete.confirm')}
            </Button>
          </>
        )
      }
    >
      <div className={styles.body}>
        <TradePill
          label={manager.name}
          {...(manager.backgroundColor ? { backgroundColor: manager.backgroundColor } : {})}
        />
        <p className={styles.text}>
          {blockedReason
            ? t(
                blockedReason === 'tags'
                  ? 'adminManagement.delete.blockedTags'
                  : 'adminManagement.delete.blockedActivities',
              )
            : t('adminManagement.delete.confirmQuestion')}
        </p>
      </div>
    </Modal>
  );
}
